import { cva } from 'class-variance-authority';
import * as React from 'react';
import { StepButtonContainer } from '~/modules/common/stepper/step-button-container';
import { StepIcon } from '~/modules/common/stepper/step-icon';
import { StepLabel } from '~/modules/common/stepper/step-label';
import type { StepSharedProps } from '~/modules/common/stepper/types';
import { useStepper } from '~/modules/common/stepper/use-stepper';
import { Collapsible, CollapsibleContent } from '~/modules/ui/collapsible';
import { cn } from '~/utils/cn';

type VerticalStepProps = StepSharedProps & {
  children?: React.ReactNode;
};

const verticalStepVariants = cva(
  [
    'relative flex flex-col transition-all duration-200',
    'data-[completed=true]:not-last:after:bg-primary',
    'data-[invalid=true]:not-last:after:bg-destructive',
  ],
  {
    variants: {
      variant: {
        circle: cn(
          'not-last:gap-(--step-gap) not-last:pb-(--step-gap)',
          "not-last:after:w-0.5 not-last:after:bg-border not-last:after:content-['']",
          'not-last:after:inset-x-[calc(var(--step-icon-size)/2)]',
          'not-last:after:absolute',
          'not-last:after:top-[calc(var(--step-icon-size)+var(--step-gap))]',
          'not-last:after:bottom-(--step-gap)',
          'not-last:after:transition-all not-last:after:duration-200',
        ),
        line: 'mb-4 flex-1 border-t-0',
      },
    },
  },
);

function VerticalStepBase(props: VerticalStepProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    children,
    index,
    isCompletedStep,
    isCurrentStep,
    label,
    description,
    isKeepError,
    icon,
    hasVisited,
    state,
    checkIcon: checkIconProp,
    errorIcon: errorIconProp,
    onClickStep,
  } = props;

  const {
    checkIcon: checkIconContext,
    errorIcon: errorIconContext,
    isError,
    isLoading,
    variant,
    clickable,
    expandVerticalSteps,
    styles,
    scrollTracking,
    orientation,
    steps,
    isLastStep: isLastStepCurrentStep,
    previousActiveStep,
  } = useStepper();

  const opacity = hasVisited ? 1 : 0.8;
  const localIsLoading = isLoading || state === 'loading';
  const localIsError = isError || state === 'error';

  const isLastStep = index === steps.length - 1;

  const active = variant === 'line' ? isCompletedStep || isCurrentStep : isCompletedStep;
  const checkIcon = checkIconProp || checkIconContext;
  const errorIcon = errorIconProp || errorIconContext;

  const renderChildren = () => {
    if (!expandVerticalSteps) {
      return (
        <Collapsible open={isCurrentStep}>
          <CollapsibleContent
            ref={(node) => {
              if (
                // Prevents initial auto-scroll when the stepper isn't at the top of the view.
                scrollTracking &&
                ((index === 0 && previousActiveStep && previousActiveStep === steps.length) || (index && index > 0))
              ) {
                node?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              }
            }}
            className="overflow-hidden data-closed:animate-collapsible-up data-open:animate-collapsible-down"
          >
            {children}
          </CollapsibleContent>
        </Collapsible>
      );
    }
    return children;
  };

  return (
    <div
      ref={ref}
      className={cn(
        'stepper__vertical-step',
        verticalStepVariants({
          variant: variant?.includes('circle') ? 'circle' : 'line',
        }),
        isLastStepCurrentStep && 'gap-(--step-gap)',
        styles?.['vertical-step'],
      )}
      data-optional={steps[index || 0]?.optional}
      data-completed={isCompletedStep}
      data-active={active}
      data-clickable={clickable || !!onClickStep}
      data-invalid={localIsError}
    >
      {steps.length > 1 && (
        <div
          data-vertical={true}
          data-active={active}
          className={cn(
            'stepper__vertical-step-container',
            'flex items-center',
            variant === 'line' && 'border-s-[.19rem] py-2 ps-3 data-[active=true]:border-primary',
            styles?.['vertical-step-container'],
          )}
        >
          <StepButtonContainer {...{ isLoading: localIsLoading, isError: localIsError, ...props }}>
            <StepIcon
              {...{
                index,
                isError: localIsError,
                isLoading: localIsLoading,
                isKeepError,
                isCurrentStep,
                isCompletedStep,
              }}
              icon={icon}
              checkIcon={checkIcon}
              errorIcon={errorIcon}
            />
          </StepButtonContainer>

          <StepLabel label={label} description={description} {...{ isCurrentStep, opacity }} />
        </div>
      )}
      <div
        className={cn(
          'stepper__vertical-step-content',
          !isLastStep && 'min-h-4',
          variant !== 'line' && 'max-sm:relative max-sm:z-1 sm:ps-(--step-icon-size)',
          variant === 'line' && orientation === 'vertical' && 'min-h-0',
          styles?.['vertical-step-content'],
        )}
      >
        {renderChildren()}
      </div>
    </div>
  );
}

export const VerticalStep = React.forwardRef<HTMLDivElement, VerticalStepProps>(VerticalStepBase);
